// TemplatesPanel — author the documents this company issues, for the
// CompanyDetail tab (#62 phase 2).
//
// Phase 1 gave a merchant control of how a document LOOKS (ThemePanel). This
// gives them control of what is ON it: its title, its named fields, and whether
// it computes tax.
//
// THREE THINGS THIS PANEL MUST SAY OUT LOUD, because each is invisible in the
// data and each leads an operator to a wrong belief they will act on:
//
//   1. EDITING A TEMPLATE DOES NOT CHANGE DOCUMENTS ALREADY ISSUED. Every
//      document freezes the definition it was minted with. An operator who
//      thinks otherwise will fix a label here and tell a customer their receipt
//      is corrected.
//   2. "Computes tax" is a BACKSTOP, not a preference. Unticked forces tax to
//      zero whatever the company's scheme says. Untick it on a real invoice and
//      output tax is under-stated on a frozen document — silent until an audit.
//      That direction gets a loud warning; the opposite does not need one,
//      because an over-charged customer complains the same day.
//   3. Retiring is not deleting. A retired template stops resolving, so any
//      company still pointing at that key silently falls back to a GENERIC
//      document rather than failing visibly.
//
// Builtins are listed alongside and are read-only: they are shared by every
// company, and a per-company edit to one would change documents for merchants
// who never asked.
import { useMemo, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { I } from "@/components/Icons";
import { Pill, EmptyState } from "@/components/UI";
import { ConfirmDialog, type ConfirmRequest } from "@/components/ConfirmDialog";
import { apiErrorMessage } from "@/utils/apiError";
import { useUI } from "@/store/ui";
import {
  listCompanyTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  templateKeyError,
  fieldKeyError,
  taxabilityWarning,
  TEMPLATE_DOC_TYPES,
  TEMPLATE_DOC_TYPE_LABELS,
  MAX_TEMPLATE_TITLE_LEN,
  MAX_TEMPLATE_FIELDS,
  MAX_FIELD_LABEL_LEN,
  type DocumentTemplate,
  type TemplateField,
  type TemplateDocType,
  type TemplateWrite,
} from "@/api/templates";

type Draft = TemplateWrite & { fields: TemplateField[] };

const EMPTY_DRAFT: Draft = {
  key: "",
  doc_type: "invoice",
  title: "",
  taxable: true,
  fields: [],
  active: true,
};

export function TemplatesPanel({
  companyId,
  companyTaxation,
}: {
  companyId: string;
  companyTaxation: string;
}) {
  const qc = useQueryClient();
  const addToast = useUI((s) => s.addToast);
  const [editing, setEditing] = useState<DocumentTemplate | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [confirm, setConfirm] = useState<ConfirmRequest | null>(null);

  const listQ = useQuery({
    queryKey: ["templates", companyId],
    queryFn: () => listCompanyTemplates(companyId),
  });

  const fail = (err: unknown, title: string) =>
    addToast({ title, body: apiErrorMessage(err, ""), variant: "error" });

  const done = (title: string) => {
    addToast({ title, variant: "success" });
    // The picker on the Identity tab and the preview both read this list.
    void qc.invalidateQueries({ queryKey: ["templates", companyId] });
    void qc.invalidateQueries({ queryKey: ["preview", companyId] });
    setDraft(null);
    setEditing(null);
  };

  const create = useMutation({
    mutationFn: (d: Draft) => createTemplate(companyId, d),
    onSuccess: () => done("Template created"),
    onError: (e) => fail(e, "Could not create the template"),
  });

  const save = useMutation({
    mutationFn: ({ id, d }: { id: string; d: Draft }) => updateTemplate(id, d),
    onSuccess: () => done("Template saved"),
    onError: (e) => fail(e, "Could not save the template"),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteTemplate(id),
    onSuccess: () => done("Template deleted"),
    onError: (e) => fail(e, "Could not delete the template"),
  });

  const { builtins, own } = useMemo(() => {
    const all = listQ.data ?? [];
    return {
      builtins: all.filter((t) => t.built_in),
      own: all.filter((t) => !t.built_in),
    };
  }, [listQ.data]);

  const keyErr = draft ? templateKeyError(draft.key) : null;
  const fieldErrs = draft
    ? draft.fields.map(
        (f) =>
          fieldKeyError(f.key) ??
          ((f.label ?? "").trim() === "" ? "A label is required." : null),
      )
    : [];
  const dupKey =
    draft != null &&
    new Set(draft.fields.map((f) => f.key.trim().toLowerCase())).size !==
      draft.fields.length;
  const titleErr =
    draft != null && draft.title.trim() === "" ? "A title is required." : null;
  // go-pim refuses this outright: a payment confirmation acknowledges that money
  // moved, it is not evidence of a taxable supply.
  const confirmationTaxErr =
    draft != null && draft.doc_type === "payment_confirmation" && draft.taxable
      ? "A payment confirmation is never taxable — it acknowledges a payment, it does not record a supply."
      : null;

  const blocked =
    !draft ||
    keyErr != null ||
    titleErr != null ||
    confirmationTaxErr != null ||
    dupKey ||
    fieldErrs.some((e) => e != null);

  const taxWarn = draft
    ? taxabilityWarning(
        { doc_type: draft.doc_type, taxable: draft.taxable },
        companyTaxation,
      )
    : null;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (blocked || !draft) return;
    if (editing?.id) save.mutate({ id: editing.id, d: draft });
    else create.mutate(draft);
  };

  const setField = (i: number, patch: Partial<TemplateField>) =>
    setDraft((d) =>
      d
        ? {
            ...d,
            fields: d.fields.map((f, j) => (j === i ? { ...f, ...patch } : f)),
          }
        : d,
    );

  if (listQ.isLoading) return <div className="muted">Loading templates…</div>;
  if (listQ.isError)
    return (
      <div className="err">
        {apiErrorMessage(listQ.error, "Could not load templates")}
      </div>
    );

  return (
    <div>
      <div
        className="card"
        style={{ padding: 12, marginBottom: 16, display: "flex", gap: 10 }}
      >
        <I.Info />
        <div style={{ fontSize: 13 }}>
          <strong>
            Editing a template never changes a document already issued.
          </strong>{" "}
          Every document keeps its own copy of the template it was created with,
          so renaming a field — or deleting the template outright — leaves
          receipts people already hold exactly as they were sent.
        </div>
      </div>

      {!draft && (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 8,
            }}
          >
            <span className="lbl">This company's templates ({own.length})</span>
            <button
              type="button"
              className="btn-primary"
              onClick={() => {
                setEditing(null);
                setDraft({ ...EMPTY_DRAFT, fields: [] });
              }}
            >
              New template
            </button>
          </div>

          {own.length === 0 ? (
            <EmptyState
              title="No templates of your own yet"
              body="The built-in templates below cover the common cases. Create one when a merchant needs their own title or their own named fields."
            />
          ) : (
            <div style={{ display: "grid", gap: 8, marginBottom: 24 }}>
              {own.map((t) => {
                const warn = taxabilityWarning(t, companyTaxation);
                return (
                  <div
                    key={t.id}
                    className="card"
                    style={{ padding: 12, display: "grid", gap: 6 }}
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        alignItems: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      <strong>{t.title}</strong>
                      <Pill tone="">
                        {TEMPLATE_DOC_TYPE_LABELS[t.doc_type]}
                      </Pill>
                      {t.taxable ? (
                        <Pill tone="info">Computes tax</Pill>
                      ) : (
                        <Pill tone="">No tax</Pill>
                      )}
                      {!t.active && <Pill tone="warn">Retired</Pill>}
                      <span style={{ flex: 1 }} />
                      <button
                        type="button"
                        onClick={() => {
                          setEditing(t);
                          setDraft({
                            key: t.key,
                            doc_type: t.doc_type,
                            title: t.title,
                            taxable: t.taxable,
                            active: t.active,
                            fields: t.fields.map((f) => ({
                              key: f.key,
                              label: f.label ?? "",
                              required: f.required ?? false,
                            })),
                          });
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setConfirm({
                            title: `Delete “${t.title}”?`,
                            body: "Documents already issued under it are unaffected — each keeps its own frozen copy. But any company still pointing at this key will quietly fall back to a generic template on its next document.",
                            confirmLabel: "Delete template",
                            destructive: true,
                            onConfirm: () => remove.mutate(t.id),
                          })
                        }
                      >
                        Delete
                      </button>
                    </div>
                    <code style={{ fontSize: 12 }}>{t.key}</code>
                    {t.fields.length > 0 && (
                      <div className="muted" style={{ fontSize: 12 }}>
                        {t.fields.map((f) => f.label || f.key).join(" · ")}
                      </div>
                    )}
                    {!t.active && (
                      <div className="muted" style={{ fontSize: 12 }}>
                        Retired: this template no longer resolves, so a company
                        still pointing at it falls back to a generic document.
                      </div>
                    )}
                    {warn && (
                      <div className="err" style={{ fontSize: 12 }}>
                        {warn}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="lbl" style={{ marginBottom: 8 }}>
            Built in ({builtins.length})
          </div>
          <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
            Shared by every company and read-only. To change one for this
            merchant, create your own with a different key.
          </div>
          <div style={{ display: "grid", gap: 6 }}>
            {builtins.map((t) => (
              <div
                key={t.key}
                className="card"
                style={{
                  padding: 10,
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <strong style={{ fontSize: 13 }}>{t.title}</strong>
                <Pill tone="">{TEMPLATE_DOC_TYPE_LABELS[t.doc_type]}</Pill>
                {t.taxable && <Pill tone="info">Computes tax</Pill>}
                <code style={{ fontSize: 11 }}>{t.key}</code>
              </div>
            ))}
          </div>
        </>
      )}

      {draft && (
        <form onSubmit={onSubmit} className="card" style={{ padding: 16 }}>
          <div className="lbl" style={{ marginBottom: 12 }}>
            {editing ? `Edit ${editing.key}` : "New template"}
          </div>

          <label style={{ display: "block", marginBottom: 8 }}>
            <span className="lbl">Key</span>
            <input
              type="text"
              value={draft.key}
              placeholder="solar_epc_invoice"
              onChange={(e) => setDraft({ ...draft, key: e.target.value })}
            />
            {keyErr ? (
              <span className="err" style={{ fontSize: 12 }}>
                {keyErr}
              </span>
            ) : (
              <span className="muted" style={{ fontSize: 12 }}>
                What the company's template setting points at. Unique within
                this company.
              </span>
            )}
          </label>

          <label style={{ display: "block", marginBottom: 8 }}>
            <span className="lbl">Title</span>
            <input
              type="text"
              value={draft.title}
              maxLength={MAX_TEMPLATE_TITLE_LEN}
              placeholder="Solar EPC Tax Invoice"
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            />
            {titleErr && (
              <span className="err" style={{ fontSize: 12 }}>
                {titleErr}
              </span>
            )}
          </label>

          <label style={{ display: "block", marginBottom: 8 }}>
            <span className="lbl">Document type</span>
            <select
              value={draft.doc_type}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  doc_type: e.target.value as TemplateDocType,
                })
              }
            >
              {TEMPLATE_DOC_TYPES.map((d) => (
                <option key={d} value={d}>
                  {TEMPLATE_DOC_TYPE_LABELS[d]}
                </option>
              ))}
            </select>
          </label>

          <label
            style={{
              display: "flex",
              gap: 8,
              alignItems: "start",
              marginBottom: 4,
            }}
          >
            <input
              type="checkbox"
              checked={draft.taxable}
              onChange={(e) =>
                setDraft({ ...draft, taxable: e.target.checked })
              }
              style={{ marginTop: 3 }}
            />
            <span>
              <span>Computes tax</span>
              <br />
              <span className="muted" style={{ fontSize: 12 }}>
                Unticked forces the tax on every document to zero, whatever the
                company's tax scheme says. That is correct for a donation
                receipt and wrong for a real invoice.
              </span>
            </span>
          </label>
          {confirmationTaxErr && (
            <div className="err" style={{ fontSize: 12, marginBottom: 8 }}>
              {confirmationTaxErr}
            </div>
          )}
          {taxWarn && (
            <div className="err" style={{ fontSize: 12, marginBottom: 8 }}>
              {taxWarn}
            </div>
          )}

          {editing && (
            <label
              style={{
                display: "flex",
                gap: 8,
                alignItems: "start",
                marginBottom: 12,
              }}
            >
              <input
                type="checkbox"
                checked={!draft.active}
                onChange={(e) =>
                  setDraft({ ...draft, active: !e.target.checked })
                }
                style={{ marginTop: 3 }}
              />
              <span>
                <span>Retired</span>
                <br />
                <span className="muted" style={{ fontSize: 12 }}>
                  Stops this template being used. Any company still pointing at
                  it falls back to a generic document rather than failing —
                  check the template setting before retiring one.
                </span>
              </span>
            </label>
          )}

          <div className="lbl" style={{ marginTop: 8, marginBottom: 6 }}>
            Fields ({draft.fields.length}/{MAX_TEMPLATE_FIELDS})
          </div>
          <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
            Named values the document prints, in this order. The key is what the
            payment system sends; the label is what a customer reads.
          </div>
          {dupKey && (
            <div className="err" style={{ fontSize: 12, marginBottom: 8 }}>
              Two fields share a key. Values arrive keyed by name, so a
              duplicate prints one field's value under the other's label.
            </div>
          )}
          {draft.fields.map((f, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                gap: 8,
                marginBottom: 6,
                alignItems: "start",
              }}
            >
              <input
                type="text"
                aria-label={`Field ${i + 1} key`}
                placeholder="site_address"
                value={f.key}
                onChange={(e) => setField(i, { key: e.target.value })}
                style={{ width: 180 }}
              />
              <input
                type="text"
                aria-label={`Field ${i + 1} label`}
                placeholder="Installation site"
                maxLength={MAX_FIELD_LABEL_LEN}
                value={f.label ?? ""}
                onChange={(e) => setField(i, { label: e.target.value })}
                style={{ flex: 1 }}
              />
              <label
                style={{
                  display: "flex",
                  gap: 4,
                  alignItems: "center",
                  fontSize: 12,
                }}
              >
                <input
                  type="checkbox"
                  checked={f.required ?? false}
                  onChange={(e) => setField(i, { required: e.target.checked })}
                />
                Required
              </label>
              <button
                type="button"
                aria-label={`Remove field ${i + 1}`}
                onClick={() =>
                  setDraft({
                    ...draft,
                    fields: draft.fields.filter((_, j) => j !== i),
                  })
                }
              >
                Remove
              </button>
              {fieldErrs[i] && (
                <span className="err" style={{ fontSize: 12 }}>
                  {fieldErrs[i]}
                </span>
              )}
            </div>
          ))}
          {draft.fields.length < MAX_TEMPLATE_FIELDS && (
            <button
              type="button"
              onClick={() =>
                setDraft({
                  ...draft,
                  fields: [
                    ...draft.fields,
                    { key: "", label: "", required: false },
                  ],
                })
              }
            >
              Add field
            </button>
          )}

          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button
              type="submit"
              className="btn-primary"
              disabled={blocked || create.isPending || save.isPending}
            >
              {create.isPending || save.isPending
                ? "Saving…"
                : editing
                  ? "Save template"
                  : "Create template"}
            </button>
            <button
              type="button"
              onClick={() => {
                setDraft(null);
                setEditing(null);
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <ConfirmDialog
        request={confirm}
        busy={remove.isPending}
        onClose={() => setConfirm(null)}
      />
    </div>
  );
}
